import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@f2e/ui/components/base"

export interface ContextVariableOption {
  value: string
  labelKey: string
}

export interface HttpRequestVariableInputProps {
  value?: string
  disabled?: boolean
  options: ContextVariableOption[]
  onChange: (nextValue: string) => void
}

const contextTokenRegex = /{{\s*user\.[^}]+}}/g

export const HttpRequestVariableInput = (
  props: HttpRequestVariableInputProps
) => {
  const { value, disabled = false, options, onChange } = props
  const { t } = useTranslation()
  const [openPopover, setOpenPopover] = useState(false)

  const selectedOption = useMemo(() => {
    if (!value) return undefined
    const directMatch = options.find((option) => option.value === value)
    if (directMatch) return directMatch
    const match = value.match(contextTokenRegex)?.[0]
    if (!match) return undefined
    return options.find((option) => option.value === match)
  }, [options, value])

  return (
    <div className="flex items-center justify-start">
      <Popover open={openPopover} onOpenChange={setOpenPopover}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 rounded-full px-3 text-xs font-medium shadow-sm"
            disabled={disabled}
          >
            <span className="truncate">
              {selectedOption
                ? t(selectedOption.labelKey)
                : options[0]
                  ? t(options[0].labelKey)
                  : ""}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-3">
          <div className="flex flex-wrap gap-2">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-700 transition hover:border-slate-300 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-slate-600 dark:hover:bg-slate-800"
                onClick={() => {
                  onChange(option.value)
                  setOpenPopover(false)
                }}
              >
                {t(option.labelKey)}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
